import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, User, Flame, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import Swal from 'sweetalert2';
import UrgentNotificationModal from './UrgentNotificationModal';
import './NotificationBell.css';

const API_URL = import.meta.env.VITE_API_URL;
let socket = null;

const getLinkLabel = (lien, clientId, dossierId) => {
    let targetUrl = lien;
    if (!targetUrl) {
        if (dossierId && clientId) targetUrl = `/bo/clients/${clientId}`;
        else if (clientId) targetUrl = `/bo/clients/${clientId}`;
    }

    if (!targetUrl) return null;

    let label = "Consulter";
    if (targetUrl.includes('/clients')) label = "Voir le client";
    else if (targetUrl.includes('/dossiers') || dossierId) label = "Voir le dossier";
    else if (targetUrl.includes('/factures')) label = "Voir la facture";
    else if (targetUrl.includes('/devis')) label = "Voir le devis";

    return { label, url: targetUrl };
};

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [showUrgentModal, setShowUrgentModal] = useState(false);
    const [userType, setUserType] = useState('');
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const showCustomAlert = (notif, isUrgentDesign = false) => {
        const timeStr = new Date(notif.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

        const customHtml = `
            <div class="custom-swal-alert">
               <div class="alert-icon-wrapper">
                   <div class="alert-icon-circle">
                       <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
                       <div class="alert-badge-dot">!</div>
                   </div>
               </div>
               <div class="alert-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="m4.93 4.93 1.41 1.41"></path><path d="m17.66 17.66 1.41 1.41"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m6.34 17.66-1.41 1.41"></path><path d="m19.07 4.93-1.41 1.41"></path></svg> 
                  ${isUrgentDesign ? 'ALERTE URGENTE' : 'NOUVELLE NOTE'}
               </div>
               <h2 class="alert-title">Vous avez une nouvelle alerte</h2>
               <hr class="alert-divider" />
               
               <div class="alert-sender-container">
                   <div class="alert-sender-left">
                       <div class="alert-avatar">
                           <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                       </div>
                       <div class="alert-sender-info">
                           <div class="alert-sender-name">${notif.auteurNom}</div>
                           <div class="alert-sender-role">Expéditeur</div>
                       </div>
                   </div>
                   <div class="alert-sender-right">
                       <div class="alert-time-icon">
                           <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                       </div>
                       <div class="alert-time-info">
                           <div class="alert-time-exact">Aujourd'hui à ${timeStr}</div>
                           <div class="alert-time-relative">il y a quelques secondes</div>
                       </div>
                   </div>
               </div>

               <div class="alert-message-box">
                   <div class="alert-message-header"><strong>${notif.auteurNom}</strong> vous a envoyé un message ${isUrgentDesign ? 'urgent' : 'important'} :</div>
                   <div class="alert-message-content">${notif.message}</div>
               </div>
            </div>
        `;

        Swal.fire({
            html: customHtml,
            showCloseButton: true,
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonText: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> J'ai compris`,
            cancelButtonText: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg> Voir les détails`,
            customClass: {
                popup: 'custom-alert-popup',
                confirmButton: 'custom-alert-confirm',
                cancelButton: 'custom-alert-cancel',
                actions: 'custom-alert-actions'
            },
            buttonsStyling: false,
            backdrop: `rgba(0,0,0,0.5)`,
            allowOutsideClick: false
        }).then((result) => {
            markAsRead(notif._id);
            if (result.dismiss === Swal.DismissReason.cancel) {
                const linkData = getLinkLabel(notif.lien, notif.clientId, notif.dossierId);
                if (linkData && linkData.url) {
                    if (linkData.url.startsWith('http://') || linkData.url.startsWith('https://')) {
                        window.open(linkData.url, '_blank');
                    } else {
                        navigate(linkData.url);
                    }
                }
            }
        });
    };

    // Fetch initial notifications and setup socket
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const storedTypeUser = localStorage.getItem('typeUser');

        if (!token || !userId) return;

        setUserType(storedTypeUser || '');

        const notifIdsRef = new Set();

        const fetchNotifications = async () => {
            try {
                const res = await axios.get(`${API_URL}/notifications`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.success) {
                    const sortedNotifs = res.data.data.sort((a, b) => {
                        if (a.isUrgent && !b.isUrgent) return -1;
                        if (!a.isUrgent && b.isUrgent) return 1;
                        return new Date(b.createdAt) - new Date(a.createdAt);
                    });

                    sortedNotifs.forEach(n => notifIdsRef.add(n._id));
                    setNotifications(sortedNotifs);
                    setUnreadCount(res.data.nonLuesCount);

                    const unreadUrgents = sortedNotifs.filter(n => n.isUrgent && n.statut === 'non_lue');
                    if (unreadUrgents.length > 0) {
                        const firstUrgent = unreadUrgents[0];
                        showCustomAlert(firstUrgent, true);
                    }
                }
            } catch (err) {
                console.error("Erreur de chargement des notifications", err);
            }
        };

        fetchNotifications();

        const SOCKET_URL = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace('/api', '') : 'http://localhost:5000';
        socket = io(SOCKET_URL, {
            query: { userId }
        });

        socket.on('new_notification', (notif) => {
            if (notifIdsRef.has(notif._id)) return;
            notifIdsRef.add(notif._id);

            setNotifications(prev => {
                const updated = [notif, ...prev];
                return updated.sort((a, b) => {
                    if (a.isUrgent && !b.isUrgent) return -1;
                    if (!a.isUrgent && b.isUrgent) return 1;
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
            });

            setUnreadCount(prev => prev + 1);

            showCustomAlert(notif, false);

            toast.info(
                <div onClick={() => handleNotificationClick(notif)} style={{ cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {notif.priorite === 'haute' && <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', padding: '3px 8px', background: '#ffedd5', color: '#ea580c', borderRadius: '12px', fontWeight: 'bold', border: '1px solid #fdba74' }}><Flame size={12} /> HAUTE</span>}
                        {notif.titre || 'Nouvelle Note'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#475569', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', width: 'fit-content', fontWeight: '600' }}>
                        <User size={14} color="#64748b" /> <span>{notif.auteurNom}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#334155', background: '#f8fafc', padding: '10px', borderRadius: '8px', borderLeft: `3px solid ${notif.priorite === 'haute' ? '#fb923c' : '#cbd5e1'}`, lineHeight: '1.4' }}>
                        {notif.message.length > 70 ? notif.message.substring(0, 70) + '...' : notif.message}
                    </div>
                    {(() => {
                        const linkData = getLinkLabel(notif.lien, notif.clientId, notif.dossierId);
                        if (linkData) {
                            return (
                                <div style={{ marginTop: '12px', fontSize: '12.5px', color: '#2563eb', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <ExternalLink size={14} /> {linkData.label} ➜
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>,
                {
                    position: "bottom-right",
                    autoClose: 6000,
                    className: notif.priorite === 'haute' ? 'toast-high-priority' : '',
                    closeOnClick: false,
                    style: { padding: '16px', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }
                }
            );
        });

        socket.on('urgent_notification', (notif) => {
            if (notifIdsRef.has(notif._id)) return;
            notifIdsRef.add(notif._id);

            setNotifications(prev => {
                const updated = [notif, ...prev];
                return updated.sort((a, b) => {
                    if (a.isUrgent && !b.isUrgent) return -1;
                    if (!a.isUrgent && b.isUrgent) return 1;
                    return new Date(b.createdAt) - new Date(a.createdAt);
                });
            });

            setUnreadCount(prev => prev + 1);
            showCustomAlert(notif, true);
        });

        return () => {
            if (socket) socket.disconnect();
        };
    }, []);

    const markAsRead = async (id, e) => {
        if (e) e.stopPropagation();
        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(`${API_URL}/notifications/${id}/lu`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setNotifications(prev => prev.map(n => n._id === id ? { ...n, statut: 'lue' } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const markAllAsRead = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(`${API_URL}/notifications/tout-lu`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setNotifications(prev => prev.map(n => ({ ...n, statut: 'lue' })));
                setUnreadCount(0);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleNotificationClick = (notif) => {
        if (notif.statut === 'non_lue') {
            markAsRead(notif._id);
        }

        const linkData = getLinkLabel(notif.lien, notif.clientId, notif.dossierId);
        if (linkData && linkData.url) {
            setIsOpen(false);
            if (linkData.url.startsWith('http://') || linkData.url.startsWith('https://')) {
                window.open(linkData.url, '_blank');
            } else {
                navigate(linkData.url);
            }
        }
    };

    const formatDate = (dateString) => {
        const d = new Date(dateString);
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="notif-bell-container" ref={dropdownRef}>
            <div className="notif-bell-icon-wrapper" onClick={() => setIsOpen(!isOpen)}>
                <Bell size={22} className="notif-bell-icon" />
                {unreadCount > 0 && (
                    <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </div>

            {isOpen && (
                <div className="notif-dropdown">
                    <div className="notif-dropdown-header">
                        <h3>Notifications</h3>
                        {unreadCount > 0 && (
                            <button onClick={markAllAsRead} className="notif-mark-all-btn" title="Tout marquer comme lu">
                                <CheckCheck size={16} />
                            </button>
                        )}
                    </div>

                    <div className="notif-list">
                        {notifications.length === 0 ? (
                            <div className="notif-empty">Aucune notification</div>
                        ) : (
                            notifications.map(notif => (
                                <div
                                    key={notif._id}
                                    className={`notif-item ${notif.statut === 'non_lue' ? 'unread' : 'read'} ${notif.isUrgent ? 'urgent' : notif.priorite === 'haute' ? 'high-priority' : 'normal-priority'}`}
                                    onClick={() => handleNotificationClick(notif)}
                                >
                                    <div className="notif-content">
                                        <div className="notif-header">
                                            <div className="notif-title-group">
                                                {notif.isUrgent && <span className="notif-badge-urgent"><AlertTriangle size={12} /> URGENT</span>}
                                                {!notif.isUrgent && notif.priorite === 'haute' && <span className="notif-badge-high"><Flame size={12} /> HAUTE</span>}
                                                <span className="notif-title">{notif.titre || 'Nouvelle Note'}</span>
                                            </div>
                                            <div className="notif-date">{formatDate(notif.createdAt)}</div>
                                        </div>

                                        <div className="notif-sender">
                                            <User size={14} className="notif-icon-user" /> <span>{notif.auteurNom}</span>
                                        </div>

                                        <div className="notif-message-box">
                                            {notif.message.length > 90 ? notif.message.substring(0, 90) + '...' : notif.message}
                                        </div>

                                        <div className="notif-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                            {(() => {
                                                const linkData = getLinkLabel(notif.lien, notif.clientId, notif.dossierId);
                                                if (linkData) {
                                                    return (
                                                        <button
                                                            className="notif-link-btn"
                                                            onClick={(e) => { e.stopPropagation(); handleNotificationClick(notif); }}
                                                        >
                                                            <ExternalLink size={14} /> {linkData.label}
                                                        </button>
                                                    );
                                                }
                                                return null;
                                            })()}

                                            {notif.statut === 'non_lue' ? (
                                                <button
                                                    className="notif-ack-btn"
                                                    style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif' }}
                                                    onClick={(e) => { e.stopPropagation(); markAsRead(notif._id, e); }}
                                                    onMouseOver={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
                                                    onMouseOut={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                                                    title="Marquer comme lu"
                                                >
                                                    <Check size={14} /> J'ai compris
                                                </button>
                                            ) : (
                                                <div style={{ padding: '8px 4px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: '500' }}>
                                                    <CheckCheck size={14} /> Lue
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    {['admin', 'moderateur'].includes(userType) && (
                        <div className="notif-dropdown-footer">
                            <button className="notif-btn-urgent" onClick={() => { setIsOpen(false); setShowUrgentModal(true); }}>
                                <AlertTriangle size={16} /> Alerte Urgente
                            </button>
                        </div>
                    )}
                </div>
            )}

            {showUrgentModal && <UrgentNotificationModal onClose={() => setShowUrgentModal(false)} />}
        </div>
    );
};

export default NotificationBell;
